import { forwardRef } from "react";
import { createLucideIcon, type LucideIcon, type LucideProps } from "lucide-react";
import { AntennaMark } from "@cntrl/ui";

/**
 * The mobile nav glyphs carried over from the legacy shell (ART-185, PO 2026-10-02): "Home; Socials should be the
 * play button that content was; Antenna needs to be the actual antenna logo; DOS should just be the clock — the
 * rest the way we have it is good." Geometry is the legacy `TabIcon` paths verbatim (24-unit viewBox); the stroke
 * stays the app's 1.5 (`Icon`, brand guide §12) — the PO asked for the SHAPES, not the old weights.
 *
 * Built with `createLucideIcon` so each is a real `LucideIcon` and renders through `Icon` like every other glyph.
 */

/** Home — the legacy house: a single outline with the door cut into it. */
export const HomeGlyph = createLucideIcon("cntrl-home", [
  ["path", { d: "M3 11l9-7 9 7v9a2 2 0 0 1-2 2h-4v-7h-6v7H5a2 2 0 0 1-2-2v-9z", key: "house" }],
]);

/** Socials — the video player with a SOLID play triangle (the one deliberate fill in the tab set). */
export const SocialsGlyph = createLucideIcon("cntrl-socials", [
  ["rect", { x: "3", y: "5", width: "18", height: "14", rx: "3", key: "screen" }],
  ["polygon", { points: "10 9 16 12 10 15", fill: "currentColor", stroke: "none", key: "play" }],
]);

/** DOS — just the clock (the centre hero tab). */
export const DosGlyph = createLucideIcon("cntrl-dos", [
  ["circle", { cx: "12", cy: "12", r: "9", key: "face" }],
  ["path", { d: "M12 7v5l3 2", key: "hands" }],
]);

/**
 * Antenna — the real Antenna brand mark, not a stand-in glyph. It is FILLED artwork on its own viewBox, so it cannot
 * be a lucide icon node; this adapter takes the `Icon` call shape (`size`, `className`, aria) and ignores the stroke
 * props, which mean nothing to a filled mark. `size` is the HEIGHT; the mark's own aspect sets the width, so it never
 * squashes into a square box.
 */
export const AntennaGlyph = forwardRef<SVGSVGElement, LucideProps>(function AntennaGlyph({ size = 20, className }, _ref) {
  const px = Number(size);
  // Centred in a SQUARE box the size of every other glyph: the mark is narrower than it is tall, and drawn bare it
  // pulled its row's label ~4px left of the rest of the More sheet.
  return (
    <span className={className} style={{ display: "inline-grid", placeItems: "center", width: px, height: px, flex: "none" }} aria-hidden="true">
      <AntennaMark size={px} />
    </span>
  );
}) as unknown as LucideIcon;
